import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

// Create a unique, descriptive key for this query
const CUSTOMER_ADDRESSES_QUERY_KEY = 'customer-addresses'

export interface CustomerAddress {
  id: number
  street_1: string
  street_2?: string
  locality?: string
  city?: string
  postalCode?: string
  isMain: boolean
  municipality?: {
    id: number
    name: string
    code: string
  }
  province?: {
    id: number
    name: string
    code: string
  }
  country?: {
    id: number
    name: string
    code: string
  }
}

export interface UseCustomerAddressesQueryParams {
  customerId: number
  params?: {
    all_data?: boolean
  }
}

/**
 * Fetch customer addresses from the API
 * Separating API call logic from the hook for better testability
 */
const fetchCustomerAddresses = async (
  customerId: number,
  params?: { all_data?: boolean }
): Promise<CustomerAddress[]> => {
  const queryParams = params
    ? `?${new URLSearchParams(params as any).toString()}`
    : ''
  const response = await APIServices.get(
    `/customer/address/${customerId}${queryParams}`
  )
  return response.data
}

/**
 * Custom hook for fetching customer addresses
 *
 * @param params - Query parameters including customerId
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export function useCustomerAddressesQuery({
  businessId,
  customerId,
  params = {},
  options,
}: UseCustomerAddressesQueryParams & {
  options?: Partial<UseQueryOptions<CustomerAddress[], AxiosError>>
  businessId: number
}) {
  // Query hook
  const queryResult = useQuery<CustomerAddress[], AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [CUSTOMER_ADDRESSES_QUERY_KEY, businessId, customerId, params],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchCustomerAddresses(customerId, params),

    // Default options that can be overridden
    enabled: !!customerId && options?.enabled !== false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    addresses: queryResult.data || [],
    isLoadingAddresses: queryResult.isLoading,
    isErrorAddresses: queryResult.isError,
    errorAddresses: queryResult.error,
    refetchAddresses: queryResult.refetch,
    isFetchingAddresses: queryResult.isFetching,
  }
}

/**
 * Helper function to get the customer addresses query key
 * Useful for cache invalidation or prefetching
 */
export const getCustomerAddressesQueryKey = (
  businessId: number,
  customerId: number,
  params?: UseCustomerAddressesQueryParams['params']
) => [CUSTOMER_ADDRESSES_QUERY_KEY, businessId, customerId, params]
